function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { div as DivMotion } from 'motion/react-client';
import { Button, Callout, useOverlayContext, useStateInRef } from 'seagull';
import { useResponsive } from "../../client-flow/contexts";
import { useStore, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { PlaidMode, usePlaidState } from "../../hooks/usePlaidState";
import { useReloginPlaidBankAccount } from "../../hooks/useReloginPlaidBankAccount";
import { useVerifyPlaidBankAccount } from "../../hooks/useVerifyPlaidBankAccount";
import { paymentMethodModel } from "../../models";
import { PlaidBankAccountStatusTypes } from "../../models/payment-method";
import { selectors } from "../../store/state-selectors";
import { helpArticles } from "../../utils/helpArticles";
import ExternalLinkButton from "../ExternalLinkButton";
import styles from "./BankAccountPanel.module.scss";
import { mapErrorToMessage } from "./errors";
import PaymentMethodPanel from "./PaymentMethodPanel";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function ManualVerificationCallout(props) {
  const {
    intent,
    hasCompleteVerificationAction,
    hasPermissionToVerify,
    text,
    id,
    verifyable,
    relationshipId,
    customerId,
    vendorId,
    setNewVerificationStatus,
    onSuccess,
    onVerify
  } = props;
  const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
  const {
    showErrorToast
  } = useOverlayContext();
  const plaidLink = usePlaidState({
    mode: {
      type: PlaidMode.Customer,
      relationshipId,
      customerId,
      vendorId,
      fundingSourceToReplace: id
    },
    onSuccess(account) {
      onSuccess(account, {
        type: 'replace',
        fundingSourceId: id
      });
    },
    onError(error) {
      showErrorToast(mapErrorToMessage(error), 'failed-restart-toast');
    }
  });
  function openPlaid() {
    plaidLink.open();
  }
  return /*#__PURE__*/_jsx(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    testId: "payment-method-callout",
    withAction: hasCompleteVerificationAction && verifyable && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        skin: "darkSecondary",
        testId: "restart-bank-payment",
        onClick: () => openPlaid(),
        size: "small",
        children: "Restart"
      }), /*#__PURE__*/_jsx(Button, {
        size: "small",
        skin: "primary",
        testId: 'payment-method-complete-verification',
        disabled: !hasPermissionToVerify,
        onClick: () => {
          verifyPlaidBankAccount({
            fundingSourceId: id,
            relationshipId,
            onVerify() {
              // Optimistic UI update.
              setNewVerificationStatus(PlaidBankAccountStatusTypes.ManuallyVerified);
              onVerify(id);
            },
            onVerifyFailed() {
              // Optimistic UI update.
              setNewVerificationStatus(PlaidBankAccountStatusTypes.VerificationFailed);
            }
          });
        },
        children: "Complete verification"
      })]
    }),
    children: text
  });
}
function AutomaticVerificationCallout(props) {
  const {
    intent,
    hasPermissionToVerify,
    text,
    id,
    verificationStatus,
    verifyable,
    relationshipId
  } = props;
  const getProps = useStateInRef(props);
  const isVendor = useStore(selectors.user.isVendor);
  const {
    api
  } = useStoreActions();
  const [loginRequired, setLoginRequired] = useState(false);
  const reloginPlaidBankAccount = useReloginPlaidBankAccount();
  const queryIsLoginRequired = useCallback(() => api.plaid.isLoginRequired(getProps().id).then(setLoginRequired), []);
  useEffect(() => {
    if (verifyable && verificationStatus === PlaidBankAccountStatusTypes.PendingAutomaticVerification) {
      queryIsLoginRequired();
    }
  }, [verificationStatus, verifyable, queryIsLoginRequired]);
  return /*#__PURE__*/_jsx(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    testId: "payment-method-callout",
    withAction: loginRequired && verifyable && /*#__PURE__*/_jsx(Button, {
      size: "small",
      skin: "darkSecondary",
      testId: 'payment-method-reconnect',
      disabled: !hasPermissionToVerify || isVendor,
      onClick: () => {
        reloginPlaidBankAccount({
          fundingSourceId: id,
          relationshipId,
          onRelogin() {
            api.plaid.loginRepaired(id).then(queryIsLoginRequired);
          }
        });
      },
      children: loginRequired ? 'Reconnect' : 'Complete verification'
    }),
    children: loginRequired ? 'Re-authentication required due to bank or account updates.' : text
  });
}
function GenericWarningCallout(props) {
  const {
    text,
    intent
  } = props;
  return /*#__PURE__*/_jsx(Callout, {
    variant: "paragraph2",
    weight: "light",
    intent: intent,
    testId: "payment-method-callout",
    children: text
  });
}
function getCallout(bankAccount, verificationStatus, isProposal, hasPermissionsToVerify, verifyable, relationshipId, customerId, vendorId, setNewVerificationStatus, onSuccess, onVerify) {
  if (paymentMethodModel.isErroredBankAccount(bankAccount.bankAccount)) {
    return /*#__PURE__*/_jsx(GenericWarningCallout, {
      intent: "error",
      text: bankAccount.bankAccount.errorMessage
    });
  } else if (paymentMethodModel.isVerifyWithStripe(bankAccount)) {
    return /*#__PURE__*/_jsx(GenericWarningCallout, {
      intent: "warning",
      text: "Verification failed. Please remove and retry or add a different payment method."
    });
  } else if (paymentMethodModel.isFailedDueToDuplicate(bankAccount)) {
    return /*#__PURE__*/_jsx(GenericWarningCallout, {
      intent: "warning",
      text: "Bank account already connected and can't be selected again. Remove this entry via the three dots menu."
    });
  } else {
    switch (verificationStatus) {
      case PlaidBankAccountStatusTypes.PendingAutomaticVerification:
        return /*#__PURE__*/_jsx(AutomaticVerificationCallout, {
          intent: isProposal ? 'info' : 'warning',
          text: isProposal ? 'Your bank account will be automatically verified within one business day.' : 'Automatic verification in progress. This process takes 1-2 business days.',
          hasPermissionToVerify: hasPermissionsToVerify,
          id: bankAccount.id,
          verificationStatus: verificationStatus,
          verifyable: verifyable && !isProposal,
          relationshipId: relationshipId,
          setNewVerificationStatus: setNewVerificationStatus
        });
      case PlaidBankAccountStatusTypes.PendingManualVerification:
        return /*#__PURE__*/_jsx(ManualVerificationCallout, {
          intent: isProposal ? 'info' : 'warning',
          text: isProposal ? 'Verification process initiated. It is required to approve the agreement to complete the verification process.' : 'Pending verification',
          hasCompleteVerificationAction: !isProposal,
          hasPermissionToVerify: hasPermissionsToVerify,
          id: bankAccount.id,
          verifyable: verifyable,
          relationshipId: relationshipId,
          customerId: customerId,
          vendorId: vendorId,
          setNewVerificationStatus: setNewVerificationStatus,
          onSuccess: onSuccess,
          onVerify: onVerify
        });
      case PlaidBankAccountStatusTypes.VerificationFailed:
      case PlaidBankAccountStatusTypes.VerificationExpired:
        return /*#__PURE__*/_jsx(GenericWarningCallout, {
          intent: "warning",
          text: "Verification failed. Please remove and retry or add a different payment method."
        });
      default:
        return null;
    }
  }
}
export default function BankAccountPanel(props) {
  const {
    isMobile
  } = useResponsive();
  const {
    bankAccount,
    isProposal,
    apiGatewayHost,
    isSelected,
    verifyable,
    hasPermissionToVerify = false,
    relationshipId,
    customerId,
    vendorId,
    onSuccess,
    onVerify,
    isPaymentMethodInMotion
  } = props;
  const {
    PAYMENT_METHODS_CHANGE_ANIMATION,
    CLIENT_ONBOARDING_NEW_FLOW
  } = useFeatureFlags();
  const [newVerificationStatus, setNewVerificationStatus] = useState(null);
  const callout = getCallout(bankAccount, newVerificationStatus || paymentMethodModel.verificationStatus(bankAccount), isProposal, hasPermissionToVerify, !!verifyable, relationshipId, customerId, vendorId, setNewVerificationStatus, onSuccess, onVerify);
  const getFooter = () => {
    if (callout != null) {
      return /*#__PURE__*/_jsx("div", {
        className: styles.footerContent,
        children: callout
      });
    }
    if (PAYMENT_METHODS_CHANGE_ANIMATION) {
      return /*#__PURE__*/_jsx(AnimatePresence, {
        children: isSelected && !isPaymentMethodInMotion && /*#__PURE__*/_jsx(DivMotion, {
          initial: {
            height: 0,
            opacity: 0
          },
          animate: {
            height: 'auto',
            opacity: 1
          },
          transition: {
            duration: 0.3,
            delay: 0.5,
            // Delay only on expand
            ease: 'easeInOut'
          },
          exit: {
            height: 0,
            opacity: 0,
            transition: {
              duration: 0.3,
              delay: 0,
              // No delay on collapse
              ease: 'easeInOut'
            }
          },
          style: {
            overflow: 'hidden'
          },
          children: /*#__PURE__*/_jsxs(Callout, {
            className: CLIENT_ONBOARDING_NEW_FLOW ? styles.companyIdsCallout : undefined,
            testId: "company-ids-callout",
            variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph3' : 'paragraph2',
            weight: "light",
            intent: "info",
            children: ["To ensure ACH transactions are processed smoothly, please ask your bank to whitelist these", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
              openInNewTab: true,
              variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph3' : 'paragraph2',
              href: helpArticles.achUnauthorizedFailures(),
              children: "company IDs"
            }), "."]
          })
        }, "company-ids-callout")
      });
    } else if (isSelected) {
      return /*#__PURE__*/_jsxs(Callout, {
        className: CLIENT_ONBOARDING_NEW_FLOW ? styles.companyIdsCallout : undefined,
        testId: "company-ids-callout",
        variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph3' : 'paragraph2',
        weight: "light",
        intent: "info",
        children: ["To ensure ACH transactions are processed smoothly, please ask your bank to whitelist these", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
          openInNewTab: true,
          variant: CLIENT_ONBOARDING_NEW_FLOW && isMobile ? 'paragraph3' : 'paragraph2',
          href: helpArticles.achUnauthorizedFailures(),
          children: "company IDs"
        }), "."]
      });
    }
    return null;
  };
  return /*#__PURE__*/_jsx(PaymentMethodPanel, _objectSpread(_objectSpread({}, props), {}, {
    title: "Bank Account",
    description: "Account number: ****".concat(paymentMethodModel.last4(bankAccount)),
    logo: /*#__PURE__*/_jsx("img", {
      "data-testid": "bank-account-logo",
      alt: "bank account logo",
      src: "".concat(apiGatewayHost, "/institution/").concat(paymentMethodModel.institutionId(bankAccount) || 'default', "/logo.png")
    }),
    footer: getFooter()
  }));
}